import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Input, Select } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty, uniq } from "lodash";
import { FILTER_PAGE, USER_TENANT_NAME } from "utils/constants";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { inputPatternTitle, inputTextPattern } from "components/Common/inputs/variables";

const { Option } = Select;

const filterOptions = [
  { name: "Customer ID", short: "CID", value: 1, id: "customerId" },
  { name: "Depot", short: "DPT", value: 2, id: "depot" },
  { name: "Primary Phone", short: "PP", value: 3, id: "customerPhone" },
];

const useStyles = makeStyles({
  filtersContainer: {
    display: "flex",
    alignItems: "center",
    height: 60,
    overflow: "auto",

    "@media (max-width: 576px)": {
      height: "auto",
      flexDirection: "column",
      width: "100%",
      overflow: "hidden",
      gap: 10,
    },

    "& .ant-select-selection-overflow": {
      flexWrap: "nowrap",
    },
  },
  filterSelect: {
    "& .ant-select": {
      minWidth: "auto!important",
    },
  },
});

const TasksFilter = props => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const { onFilter } = props;
  const [filterType, setFilterType] = useState([]);
  const {
    list: { depot },
  } = useSelector(DictionariesSelector);
  const { filters } = useStoreFilters(FILTER_PAGE.TASK_INTERVENTION);

  const selectedFilterValues = useMemo(
    () => filterType?.map(item => filterOptions[item - 1]),
    [filterType, filterOptions]
  );
  const onChange = useCallback(value => setFilterType(value), []);

  const handleOnFilter = useCallback(values => {
    const preparedData = Object.keys(values).reduce((a, item) => ({ ...a, [item]: values[item]?.trim() }), {});
    onFilter({ ...preparedData, pageSize: 1000 });
  }, []);

  useEffect(() => {
    if (!isEmpty(filters)) {
      const defaultFilter = filterOptions.filter(item => item.id in filters);
      setFilterType(defaultFilter.map(item => item.value));
      form.setFieldsValue(filters);
    }
  }, []);

  return (
    <div className="custom-filter">
      <Form form={form} layout="inline" onFinish={handleOnFilter}>
        <div className={classnames(classes.filtersContainer, selectedFilterValues.length > 2 && classes.filterSelect)}>
          <Select
            showSearch
            mode="multiple"
            tokenSeparators={[" ", ","]}
            placeholder={"Select type"}
            value={selectedFilterValues}
            onChange={onChange}
            optionLabelProp={selectedFilterValues.length > 3 ? "short" : "label"}
            allowClear
          >
            {filterOptions.map(item => (
              <Option key={item.value} title={item.name} value={item.value} label={item.name} short={item.short}>
                {item.name}
              </Option>
            ))}
          </Select>

          {!!filterType.length && filterType.includes(1) && (
            <>
              <div className="filter-divider" />
              <Form.Item name="customerId" rules={[{ required: true, message: "Please enter customer ID" }]}>
                <Input
                  style={{ width: "100%" }}
                  size={"default"}
                  placeholder={"Enter customer ID"}
                  allowClear
                  pattern={inputTextPattern}
                  title={inputPatternTitle}
                />
              </Form.Item>
            </>
          )}
          {!!filterType.length && filterType?.includes(2) && (
            <>
              <div className="filter-divider" />
              <Form.Item name="depot" rules={[{ required: true, message: "Please select depot" }]}>
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  tokenSeparators={[","]}
                  size={"default"}
                  placeholder={"Select depot"}
                  allowClear
                >
                  {depot[USER_TENANT_NAME()] &&
                    uniq(depot[USER_TENANT_NAME()])
                      ?.sort()
                      ?.map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                </Select>
              </Form.Item>
            </>
          )}
          {!!filterType.length && filterType.includes(3) && (
            <>
              <div className="filter-divider" />
              <Form.Item name="customerPhone" rules={[{ required: true, message: "Please enter customer phone" }]}>
                <Input
                  style={{ width: "100%" }}
                  placeholder={"Enter customer phone"}
                  allowClear
                  pattern={inputTextPattern}
                  title={inputPatternTitle}
                />
              </Form.Item>
            </>
          )}
        </div>
        <CustomButton type="primary" htmlType="submit" text="Filter" size="small" />
      </Form>
    </div>
  );
};

export default TasksFilter;
